import React, { useState, useEffect } from "react"
import { Box, Button, Container, useTheme } from "@mui/material"
import { motion, AnimatePresence } from "framer-motion"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import { useTheme as useCustomTheme } from "../../util/themeProvider"
import { appRoutes } from "../../util/appLinks"
import { trackEvent } from "../../util/analytics"

const MobileStickyCTA = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [hasScrolledPastHero, setHasScrolledPastHero] = useState(false)
  const { isDark } = useCustomTheme()
  const theme = useTheme()

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const heroHeight = window.innerHeight * 0.8 // Approximate hero height

      // Show CTA after scrolling past hero section
      const shouldShow = scrollY > heroHeight
      setHasScrolledPastHero(shouldShow)

      // Hide CTA when near bottom of page (to avoid footer overlap)
      const documentHeight = document.documentElement.scrollHeight
      const windowHeight = window.innerHeight
      const scrolledToBottom = scrollY + windowHeight >= documentHeight - 200

      setIsVisible(shouldShow && !scrolledToBottom)
    }

    // Throttle scroll events for better performance
    let ticking = false
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("scroll", throttledScroll)
    handleScroll() // Check initial state

    return () => window.removeEventListener("scroll", throttledScroll)
  }, [])

  const handleCTAClick = () => {
    trackEvent("CTA", "click", "mobile_sticky_cta", null, {
      cta_location: "mobile_sticky",
      cta_text: "Get Started Free",
    })
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              background: isDark
                ? "rgba(18, 18, 18, 0.95)"
                : "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderTop: `1px solid ${
                isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
              }`,
              boxShadow: isDark
                ? "0 -4px 20px rgba(0, 0, 0, 0.3)"
                : "0 -4px 20px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Container maxWidth="lg">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 2,
                  px: { xs: 2, sm: 0 },
                }}
              >
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      display: "block",
                      lineHeight: 1.2,
                    }}
                  >
                    Ready to build your resume?
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.8rem",
                      color: theme.palette.text.secondary,
                      display: "block",
                      lineHeight: 1.2,
                    }}
                  >
                    Start for free, no credit card required
                  </Box>
                </Box>
                <Button
                  href={appRoutes.signIn}
                  variant="contained"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleCTAClick}
                  sx={{
                    background: "linear-gradient(45deg, #3f51b5, #7986cb)",
                    backgroundSize: "200% auto",
                    color: "#ffffff",
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: "0 4px 14px rgba(63, 81, 181, 0.3)",
                    transition: "all 0.3s cubic-bezier(.2,.8,.2,1)",
                    minWidth: "140px",
                    "&:hover": {
                      boxShadow: "0 6px 20px rgba(63, 81, 181, 0.4)",
                      transform: "translateY(-1px)",
                      backgroundPosition: "right center",
                    },
                  }}
                >
                  Start Free
                </Button>
              </Box>
            </Container>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default MobileStickyCTA
